import { useState, useEffect } from 'react';
import { useAuthContext } from '../store/AuthContext';
import { getClientOptions } from '../services/clientService';
import { ClientOptionEnum } from '../utils/data/enums';
import Toast from '../components/ui/toasts/Toast';

const benefitAccessByIndividualId = {};
const benefitAccessInFlightByIndividualId = {};

export const useIndividualHasBenefitAccess = () => {
    const { user, adminView, multitenantFlag } = useAuthContext();
    const [hasAccess, setHasAccess] = useState(false);

    useEffect(() => {
        const individualId = user?.jwt?.ind;
        if (!individualId || adminView === true || multitenantFlag) {
            setHasAccess(false);
            return;
        }

        if (individualId in benefitAccessByIndividualId) {
            setHasAccess(benefitAccessByIndividualId[individualId]);
            return;
        }

        const fetchAccess = async () => {
            try {
                if (benefitAccessInFlightByIndividualId[individualId]) {
                    const cachedAccess =
                        await benefitAccessInFlightByIndividualId[individualId];
                    setHasAccess(cachedAccess);
                    return;
                }

                const accessPromise = (async () => {

                    // Check benefits module visibility
                    const optionsData = await getClientOptions(0, [
                        ClientOptionEnum.DisplayBenefitsModule,
                        ClientOptionEnum.BenefitsModuleIndividuals,
                    ]);

                    const displayBenefitsModuleObject = optionsData.find(
                        item => item.name === 'Display Benefits Module',
                    );

                    if (!displayBenefitsModuleObject)
                        return false;

                    if (displayBenefitsModuleObject.value === 'enabled') {
                        return true;
                    }

                    if (displayBenefitsModuleObject.value === 'testMode') {
                        const benefitsModuleIndividualsObject = optionsData.find(
                            item => item.name === 'Benefits Module Individuals',
                        );

                        if (benefitsModuleIndividualsObject) {
                            try {
                                const individualsWithAccess = JSON.parse(
                                    benefitsModuleIndividualsObject.value,
                                );
                                return individualsWithAccess.includes(individualId);
                            } catch (parseError) {
                                console.error(
                                    'Error parsing Benefits Module Individuals:',
                                    parseError,
                                );
                                return false;
                            }
                        }
                    }
                    return false;
                })();

                benefitAccessInFlightByIndividualId[individualId] = accessPromise;
                const resolvedAccess = await accessPromise;
                benefitAccessByIndividualId[individualId] = resolvedAccess;
                setHasAccess(resolvedAccess);
            } catch (error) {
                console.error('Error checking benefit access:', error);
                benefitAccessByIndividualId[individualId] = false;
                setHasAccess(false);
            } finally {
                delete benefitAccessInFlightByIndividualId[individualId];
            }
        };

        fetchAccess();
    }, [user, adminView, multitenantFlag]);

    return hasAccess;
};
